
import { useState, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import LoginRequiredDialog from "@/components/courses/LoginRequiredDialog";
import ErrorBoundary from "@/components/toolkit/ErrorBoundary";
import { AlertCircle, RefreshCw, WifiOff } from "lucide-react";

interface ToolkitPageWrapperProps {
  children: ReactNode;
  title: string;
  description: string;
  toolName: string;
  redirectUrl: string;
  errorFallback?: ReactNode;
}

const ToolkitPageWrapper = ({
  children,
  title,
  description,
  toolName,
  redirectUrl,
  errorFallback
}: ToolkitPageWrapperProps) => {
  const { user } = useAuth();
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  // Default error fallback if none provided
  const defaultErrorFallback = (
    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
      <div className="flex items-center justify-center mb-4">
        <WifiOff className="h-8 w-8 text-red-500 mr-2" />
        <h2 className="text-2xl font-bold">Network Connection Error</h2>
      </div>
      <p className="text-muted-foreground mb-6">
        We're having trouble connecting to our AI services.
        This may be due to network issues or service unavailability.
      </p>
      <div className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
        <p className="font-medium mb-2">Please try these steps:</p>
        <ul className="list-disc pl-5 space-y-1 text-left">
          <li>Check that you have a stable internet connection</li>
          <li>Make sure you're not using a VPN that might block our services</li>
          <li>Try refreshing the page</li>
          <li>If the problem persists, try again in a few minutes</li>
        </ul>
      </div>
      <div className="flex justify-center gap-4">
        <Button 
          onClick={() => window.location.reload()}
          className="flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh Page
        </Button>
      </div>
    </div>
  );

  return (
    <div className="container py-12 max-w-5xl">
      <div className="space-y-6">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold tracking-tight">{title}</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {description}
          </p>
        </div>

        <div className="mt-6">
          {user ? (
            <ErrorBoundary fallback={errorFallback || defaultErrorFallback}>
              {children}
            </ErrorBoundary>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Login Required</h2>
              <p className="text-muted-foreground mb-6">
                Please login to use the {toolName}.
              </p>
              <Button 
                onClick={() => setLoginDialogOpen(true)}
                className="px-6"
              >
                Login to Continue
              </Button>
            </div>
          )}
        </div>
      </div>

      <LoginRequiredDialog 
        isOpen={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        redirectUrl={redirectUrl}
        message={`Please log in to use the ${toolName}.`}
      />
    </div>
  );
};

export default ToolkitPageWrapper;
